import { useMemo, useCallback, useRef } from 'react';
import { Address as BaseAddress } from '@commercetools/frontend-domain-types/account/Address';
import { UserManager, UserManagerSettings } from 'oidc-client-ts';
import useSWR, { mutate } from 'swr';
import { sdk } from 'sdk';
import { Account, Address } from 'types/account';
import { revalidateOptions } from 'frontastic';
import { GetAccountResult, RegisterAccount, UpdateAccount, UseAccountReturn } from './types';
import { RedirectState } from 'types/oidc';
import { isSigninCallbackPage, isSignoutCallbackPage } from 'helpers/utils/oidc';

const useAccount = (): UseAccountReturn => {
  const FALLBACK_REDIRECT_URL_KEY = 'fallback-redirect-url';
  const extensions = sdk.composableCommerce;

  const result = useSWR('/action/account/getAccount', extensions.account.getAccount, revalidateOptions);

  const revalidateAccountData = useCallback(() => {
    mutate('/action/account/getAccount');
  }, []);

  const userManagerRef = useRef<UserManager | null>(null);

  const getUserManager = async () => {
    if (!userManagerRef.current) {
      const res = await sdk.callAction<UserManagerSettings>({
        actionName: 'oidc/getConfiguration',
      });

      const config = res.isError ? ({} as UserManagerSettings) : res.data;
      userManagerRef.current = new UserManager(config);
    }

    return userManagerRef.current;
  };

  const handleLogin = async () => {
    await sdk.callAction({ actionName: 'cart/setAnonymousCartId' });
    const userManager = await getUserManager();

    if (isSigninCallbackPage()) {
      const user = await userManager.signinCallback();

      if (!user) {
        return;
      }

      await sdk.callAction({
        actionName: 'oidc/setSessionToken',
        payload: { profile: user.profile },
      });
      await sdk.callAction({ actionName: 'cart/mergeCarts' });
      // TODO(https://nikoninc.atlassian.net/browse/REPLATFORM-3464): handle inventory error messages
      await sdk.callAction({ actionName: 'cart/validateCartInventory' });
      
      if (typeof window !== 'undefined') {
        const state = user.state as RedirectState;
        window.location.replace(state.applicationRedirectUrl || '/');
      }
    } else {
      if (typeof window !== 'undefined') {
        const redirectUrl = window.sessionStorage.getItem(FALLBACK_REDIRECT_URL_KEY);
        window.sessionStorage.removeItem(FALLBACK_REDIRECT_URL_KEY);
        window.location.replace(redirectUrl || '/');
      }
    }
  };

  const handleLogout = async () => {
    const userManager = await getUserManager();

    if (isSignoutCallbackPage()) {
      const signoutResponse = await userManager.signoutCallback();

      if (!signoutResponse) {
        return;
      }

      await extensions.account.logout();

      mutate('/action/account/getAccount');
      mutate('/action/cart/getCart');
      mutate('/action/wishlist/getWishlist');

      // if we're on the server, we can't redirect
      if (typeof window !== 'undefined') {
        const state = signoutResponse.userState as RedirectState;
        window.location.replace(state.applicationRedirectUrl || '/');
      }
    } else {
      if (typeof window !== 'undefined') {
        const redirectUrl = window.sessionStorage.getItem(FALLBACK_REDIRECT_URL_KEY);
        window.sessionStorage.removeItem(FALLBACK_REDIRECT_URL_KEY);
        window.location.replace(redirectUrl || '/');
      }
    }
  };

  const data = useMemo(() => {
    if (result.isValidating) return { loggedIn: false, accountLoading: true };

    if (result.data?.isError) return { loggedIn: false, accountLoading: false, error: result.error };

    const account = (result.data?.data as GetAccountResult)?.account as Account;

    if (account?.accountId) return { account, loggedIn: true, accountLoading: false };

    return {
      loggedIn: false,
      account: undefined,
      accountLoading: false,
      error: result.error,
    };
  }, [result]);

  const shippingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? []).filter((address) =>
      data.account.shippingAddressIds?.includes(address.id || ''),
    );
  }, [data.account]);

  const billingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? []).filter((address) =>
      data.account.shippingAddressIds?.includes(address.id || ''),
    );
  }, [data.account]);

  const defaultShippingAddress = useMemo(() => {
    return data.account?.addresses?.find((address) => address.id === data.account.defaultShippingAddress);
  }, [data.account]);

  const defaultBillingAddress = useMemo(() => {
    return data.account?.addresses?.find((address) => address.id === data.account.defaultBillingAddress);
  }, [data.account]);

  const login = async () => {
    await sdk.callAction({ actionName: 'cart/setAnonymousCartId' });

    try {
      const userManager = await getUserManager();
      const state: RedirectState = {
        applicationRedirectUrl: window.location.href,
      };

      window.sessionStorage.setItem(FALLBACK_REDIRECT_URL_KEY, window.location.href || '/');

      return userManager.signinRedirect({ state });
    } catch (error) {
      console.error(error);
    }
  };

  const initiateAuthentication = async (redirectUrl = '') => {
    const userManager = await getUserManager();
    const returnUrl = redirectUrl !== '' ? redirectUrl : window?.frames?.top?.document.referrer;
    const state: RedirectState = {
      applicationRedirectUrl: returnUrl || '/',
    };

    window.sessionStorage.setItem(FALLBACK_REDIRECT_URL_KEY, window.location.href || '/');

    return userManager.signinRedirect({ state });
  };

  const loginCT = async (email: string, password: string, remember?: boolean): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
      remember,
    };

    const res = await extensions.account.login(payload);

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');

    return res.isError ? ({} as Account) : res.data;
  };

  const logout = useCallback(async () => {
    const userManager = await getUserManager();
    const redirectUrl = window.location.pathname.startsWith('/account') ? '/' : window.location.href;
    const state: RedirectState = {
      applicationRedirectUrl: redirectUrl,
    };

    window.sessionStorage.setItem(FALLBACK_REDIRECT_URL_KEY, window.location.href || '/');

    return userManager.signoutRedirect({ state });
  }, []);

  const register = useCallback(async (account: RegisterAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.register(account);

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const confirm = useCallback(async (token: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.confirm({ token });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestConfirmationEmail = useCallback(async (email: string, password: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
    };

    await extensions.account.requestConfirmationEmail(payload);
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.changePassword({ oldPassword, newPassword });

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
    };

    await extensions.account.requestResetPassword(payload);
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.resetPassword({ token, newPassword });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const update = useCallback(async (account: UpdateAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAccount(account);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addIsSubscribedType = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || response.data.loggedIn === false) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addIsSubscribedType',
      payload: { account: response.data.account },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateSubscription = useCallback(async (isSubscribed: boolean) => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || response.data.loggedIn === false) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/updateSubscription',
      payload: { account: response.data.account, isSubscribed },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addAddress(address);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addShippingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || response.data.loggedIn === false) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addShippingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addBillingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || response.data.loggedIn === false) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addBillingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateAddress = useCallback(async (address: Address): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAddress(address);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const removeAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.removeAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultBillingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultBillingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultShippingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultShippingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const verifyAddress = useCallback(
    async (address: BaseAddress): Promise<{ originalAddress: BaseAddress; suggestedAddress: BaseAddress }> => {
      const res = await sdk.callAction({
        actionName: 'cybersource/verifyAddress',
        payload: { address },
      });

      mutate('/action/cybersource/verifyAddress');

      if (res.isError) {
        throw new Error(res.error?.message);
      }
      return res.data as { originalAddress: BaseAddress; suggestedAddress: BaseAddress };
    },
    [],
  );

  return {
    ...data,
    shippingAddresses,
    billingAddresses,
    defaultShippingAddress,
    defaultBillingAddress,
    login,
    initiateAuthentication,
    loginCT,
    logout,
    handleLogin,
    handleLogout,
    register,
    confirm,
    requestConfirmationEmail,
    changePassword,
    requestPasswordReset,
    resetPassword,
    update,
    addIsSubscribedType,
    updateSubscription,
    addAddress,
    addBillingAddress,
    addShippingAddress,
    updateAddress,
    removeAddress,
    revalidateAccountData,
    setDefaultBillingAddress,
    setDefaultShippingAddress,
    verifyAddress,
  };
};

export default useAccount;
